/**
 * App — main SPA component.
 * Renders loading screen → auth → AppShell + active view.
 */

import { useState, useCallback } from "react";
import { LoadingScreen } from "./components/LoadingScreen";
import { AppShell, type ViewKey } from "./components/AppShell";
import { clearJWT, getJWT } from "./lib/portal-session";
import { DashboardView } from "./views/DashboardView";
import { WorkPacketView } from "./views/WorkPacketView";
import { AgentChatView } from "./views/AgentChatView";
import { OffersView } from "./views/OffersView";
import { CampaignsView } from "./views/CampaignsView";
import { SequencesView } from "./views/SequencesView";
import { ContactsView } from "./views/ContactsView";
import { IntegrationsView } from "./views/IntegrationsView";
import { SurfacesView } from "./views/SurfacesView";
import { RegisterView } from "./views/RegisterView";
import { InboxView } from "./views/InboxView";
import { ConveyanceView } from "./views/ConveyanceView";
import { ProfileView } from "./views/ProfileView";
import { CommunityView } from "./views/CommunityView";
import { SettingsView } from "./views/SettingsView";
import { OperatorFleetView } from "./views/OperatorFleetView";
import { OperatorCampaignsView } from "./views/OperatorCampaignsView";
import { HighlightsView } from "./views/HighlightsView";

export function App() {
  const [authed, setAuthed] = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [activeView, setActiveView] = useState<ViewKey>("dashboard");
  const [pendingCount, setPendingCount] = useState(0);

  const handleReady = useCallback((jwt: string, tenantId: string | null, role: "client" | "principal") => {
    setAuthed(true);
    setIsOperator(role === "principal");
    if (role === "principal") {
      setActiveView("operator-fleet");
    }
  }, []);

  const handleSignOut = useCallback(() => {
    clearJWT();
    setAuthed(false);
    setIsOperator(false);
  }, []);

  // Check if already authed (e.g., page refresh with valid JWT)
  if (!authed && getJWT()) {
    // Restore session — treat as authed with default client role
    setAuthed(true);
  }

  if (!authed) {
    return <LoadingScreen onReady={handleReady} />;
  }

  return (
    <AppShell
      activeView={activeView}
      onViewChange={setActiveView}
      isOperator={isOperator}
      onSignOut={handleSignOut}
      pendingCount={pendingCount}
    >
      {activeView === "dashboard" && <DashboardView isOperator={isOperator} />}
      {activeView === "work-packet" && <WorkPacketView isOperator={isOperator} />}
      {activeView === "agent-chat" && <AgentChatView isOperator={isOperator} />}
      {activeView === "offers" && <OffersView isOperator={isOperator} />}
      {activeView === "campaigns" && <CampaignsView isOperator={isOperator} />}
      {activeView === "highlights" && (
        <HighlightsView
          isOperator={isOperator}
          onOpenCampaigns={() => setActiveView("campaigns")}
        />
      )}
      {activeView === "sequences" && <SequencesView isOperator={isOperator} />}
      {activeView === "contacts" && <ContactsView isOperator={isOperator} />}
      {activeView === "integrations" && <IntegrationsView isOperator={isOperator} />}
      {activeView === "surfaces" && <SurfacesView isOperator={isOperator} />}
      {activeView === "register" && <RegisterView isOperator={isOperator} />}
      {activeView === "inbox" && <InboxView isOperator={isOperator} onPendingCount={setPendingCount} />}
      {activeView === "api-keys" && <IntegrationsView isOperator={isOperator} />}
      {activeView === "conveyance" && <ConveyanceView isOperator={isOperator} />}
      {activeView === "profile" && <ProfileView isOperator={isOperator} />}
      {activeView === "community" && <CommunityView isOperator={isOperator} />}
      {activeView === "settings" && <SettingsView isOperator={isOperator} />}
      {activeView === "operator-fleet" && isOperator && <OperatorFleetView />}
      {activeView === "operator-campaigns" && isOperator && <OperatorCampaignsView />}
    </AppShell>
  );
}
